import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/html/class-name'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        destructiveOutline:
          'border border-destructive text-destructive shadow-xs hover:bg-destructive/10 hover:text-destructive',
        outline:
          'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline transition-all duration-200 rounded-md px-2 py-1',
        linkPrimary:
          'text-primary underline-offset-4 hover:underline transition-all duration-200 rounded-md px-2 py-1',
        primary: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        primaryOutline:
          'border border-primary text-primary shadow-xs hover:bg-primary/10 hover:text-primary',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xsm: 'h-7 rounded-md gap-1.5 px-2 has-[>svg]:px-1.5',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  hide = false,
  disabled = false,
  children,
  position = 'relative',
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
    hide?: boolean
    position?: 'relative' | 'static' | 'absolute'
  }) {
  const Comp = asChild ? Slot : 'button'

  if (hide) {
    return null
  }

  return (
    <Comp
      data-slot="button"
      data-loading={loading || undefined}
      className={cn(
        buttonVariants({ variant, size, className }),
        position,
        loading && 'opacity-80 pointer-events-none'
      )}
      disabled={disabled}
      {...props}
    >
      {!loading && children}
      {loading && (
        <>
          <div className="invisible">{children}</div>
          <Loader2 className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-6 animate-spin text-current z-20 pointer-events-none" />
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
